/**
 * Owner: Masa Bayu
 * File: pdf-geometry-object.ts
 * Date:Time: 2026-09-30 14:15
 * Description: One uniformly scaled PDF text matrix into existing IR geometry fields.
 */

import type { QXDocumentIR } from '@qx/qx-document-ir';
import { advanceUnitsForText, resolvePdfFontGlyphWidths } from './pdf-glyph-widths';
import { parsePdfLayoutToIR } from './pdf-layout-parse';

export type QXPdfGeometryBox = {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
};

export type QXPdfGeometryImport = {
  readonly document: QXDocumentIR;
  readonly sourceObjectId: string;
  readonly text: string;
  readonly pageNumber: number;
  readonly showingIndex: number;
  readonly matrix: readonly [number, number, number, number, number, number];
  readonly tfSize: number;
  readonly userSize: number;
  readonly fontName: string;
  readonly bold: boolean;
  readonly italic: boolean;
  readonly placed: QXPdfGeometryBox;
};

const latin1 = new TextDecoder('latin1');

type Chosen = {
  readonly index: number;
  readonly font: string;
  readonly tfSize: number;
  readonly scale: number;
  readonly x: number;
  readonly pdfY: number;
  readonly text: string;
};

function bytesOf(text: string): Uint8Array {
  return Uint8Array.from(text, (char) => char.charCodeAt(0));
}

function indexOfBytes(haystack: Uint8Array, needle: Uint8Array, from = 0): number {
  const last = haystack.length - needle.length;
  for (let index = from; index <= last; index += 1) {
    let matched = true;
    for (let offset = 0; offset < needle.length; offset += 1) {
      if (haystack[index + offset] !== needle[offset]) {
        matched = false;
        break;
      }
    }
    if (matched) return index;
  }
  return -1;
}

function textOf(file: Uint8Array, start: number, end: number): string {
  return latin1.decode(file.subarray(start, end));
}

function readStream(file: Uint8Array, id: number): { readonly dict: string; readonly stream: Uint8Array | null } {
  const marked = indexOfBytes(file, bytesOf(`\n${String(id)} 0 obj`));
  const at = marked >= 0 ? marked + 1 : 0;
  if (marked < 0 && indexOfBytes(file, bytesOf(`${String(id)} 0 obj`)) !== 0) {
    throw new Error(`PDF object ${String(id)} is missing`);
  }
  const slice = file.subarray(at);
  const streamAt = indexOfBytes(slice, bytesOf('stream'));
  const endAt = indexOfBytes(slice, bytesOf('endobj'));
  if (streamAt < 0 || (endAt >= 0 && endAt < streamAt)) {
    return { dict: textOf(slice, 0, endAt < 0 ? slice.length : endAt), stream: null };
  }
  const dict = textOf(slice, 0, streamAt);
  const length = dict.match(/\/Length\s+(\d+)/);
  if (!length?.[1]) throw new Error(`PDF object ${String(id)} stream has no length`);
  let dataAt = streamAt + 'stream'.length;
  if (slice[dataAt] === 0x0d) dataAt += 1;
  if (slice[dataAt] === 0x0a) dataAt += 1;
  return { dict, stream: slice.subarray(dataAt, dataAt + Number(length[1])) };
}

function pdfName(raw: string): string {
  return raw.replace(/#([0-9A-Fa-f]{2})/g, (_, hex: string) => String.fromCharCode(Number.parseInt(hex, 16)));
}

function literalOf(block: string): string {
  let text = '';
  for (const item of block.matchAll(/\((?:\\.|[^)\\])*\)/g)) {
    text += (item[0] ?? '').slice(1, -1).replace(/\\(.)/g, '$1');
  }
  return text;
}

function scaledShowing(content: string): Chosen {
  const showings = [...content.matchAll(/BT([\s\S]*?)ET/g)];
  for (let index = 0; index < showings.length; index += 1) {
    const block = showings[index]?.[1] ?? '';
    const face = block.match(/\/(T1_0|F\d+)\s+([\d.]+)\s+Tf/);
    const place = block.match(/([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)\s+Tm/);
    const text = literalOf(block).trim();
    if (!face?.[1] || !face[2] || !place?.[1] || !place[2] || !place[3] || !place[4] || !place[5] || !place[6]) continue;
    if (!/[A-Za-z]{3,}/.test(text)) continue;
    const a = Number(place[1]);
    const b = Number(place[2]);
    const c = Number(place[3]);
    const d = Number(place[4]);
    if (b !== 0 || c !== 0 || a !== d || a === 1 || a <= 0) continue;
    return {
      index,
      font: face[1],
      tfSize: Number(face[2]),
      scale: a,
      x: Number(place[5]),
      pdfY: Number(place[6]),
      text,
    };
  }
  throw new Error('Uncompressed page has no uniformly scaled text matrix');
}

/**
 * Reads one axis-aligned, uniformly scaled text showing into existing IR fields.
 * A rotated or non-uniform matrix is refused. Those coefficients have no IR field.
 */
export function importOnePdfGeometry(bytes: Uint8Array, pageNumber: number): QXPdfGeometryImport {
  if (!Number.isInteger(pageNumber) || pageNumber < 1) throw new Error('pageNumber must be a positive integer');
  const file = Uint8Array.from(bytes);
  const boxAt = indexOfBytes(file, bytesOf('/MediaBox'));
  if (boxAt < 0) throw new Error('PDF page has no MediaBox');
  const box = textOf(file, boxAt, boxAt + 160).match(/\/MediaBox\s*\[\s*[\d.]+\s+[\d.]+\s+([\d.]+)\s+([\d.]+)\s*\]/);
  const pageWidth = Number(box?.[1] ?? 0);
  const pageHeight = Number(box?.[2] ?? 0);
  if (pageWidth <= 0 || pageHeight <= 0) throw new Error('PDF MediaBox is empty');
  const fileText = textOf(file, 0, file.length);
  const contentsId = Number(fileText.match(/\/Contents\s+(\d+)\s+0\s+R/)?.[1] ?? 0);
  if (!contentsId) throw new Error('PDF page has no content stream');
  const content = readStream(file, contentsId);
  if (/\/Filter\b/.test(content.dict) || !content.stream) throw new Error('PDF content stream is not plain text');
  const chosen = scaledShowing(textOf(content.stream, 0, content.stream.length));
  const fontId = Number(fileText.match(new RegExp(`\\/${chosen.font}\\s+(\\d+)\\s+0\\s+R`))?.[1] ?? 0);
  if (!fontId) throw new Error(`PDF font ${chosen.font} has no object`);
  const font = readStream(file, fontId).dict;
  const base = font.match(/\/BaseFont\s*\/([^\s/]+)/)?.[1];
  if (!base) throw new Error('PDF font has no BaseFont');
  const fontName = pdfName(base);
  const glyph = resolvePdfFontGlyphWidths(font, (id) => readStream(file, id).dict);
  const units = advanceUnitsForText(glyph, chosen.text);
  const userSize = chosen.tfSize * chosen.scale;
  const placed = {
    x: chosen.x,
    y: pageHeight - chosen.pdfY - userSize,
    width: (units * userSize) / 1000,
    height: userSize,
  };
  const sourceObjectId = `txt_${chosen.font}_${String(chosen.index)}`;
  const bold = fontName.endsWith(',Bold') || fontName.endsWith('-Bold') || fontName.endsWith(' Bold');
  const italic = /Italic/.test(fontName);
  const document = parsePdfLayoutToIR({
    sourceHash: 'sha256:qxeditor-poc-geometry-mirror-01',
    pages: [
      {
        pageNumber,
        width: pageWidth,
        height: pageHeight,
        blocks: [
          {
            id: sourceObjectId,
            kind: 'text',
            text: chosen.text,
            bbox: placed,
            lines: [
              {
                text: chosen.text,
                bbox: placed,
                runs: [
                  {
                    text: chosen.text,
                    fontName,
                    fontSize: userSize,
                    bold,
                    italic,
                    bbox: placed,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  });
  return {
    document,
    sourceObjectId,
    text: chosen.text,
    pageNumber,
    showingIndex: chosen.index,
    matrix: [chosen.scale, 0, 0, chosen.scale, chosen.x, chosen.pdfY],
    tfSize: chosen.tfSize,
    userSize,
    fontName,
    bold,
    italic,
    placed,
  };
}
